import { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_a4b1710a-e8aa-40f2-90ed-6aa15c96d321/artifacts/w14lowsf_Disen%CC%83o%20sin%20ti%CC%81tulo.png";

// Use relative URL for API calls so it works on any domain
const getApiUrl = () => {
  if (window.location.hostname.includes('preview.emergentagent.com')) {
    return process.env.REACT_APP_BACKEND_URL || '';
  }
  return '';
};

const CompletarPerfil = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingUser, setIsLoadingUser] = useState(true);
  const [username, setUsername] = useState("");

  useEffect(() => {
    // Check for session token in URL (from Google OAuth callback)
    const params = new URLSearchParams(window.location.search);
    const sessionToken = params.get('session');
    if (sessionToken) {
      localStorage.setItem("jglinks_session", sessionToken);
      window.history.replaceState({}, document.title, window.location.pathname);
    }
    
    // Always fetch user to ensure we have fresh data
    fetchUser();
  }, []);

  const fetchUser = async () => {
    const token = localStorage.getItem("jglinks_session");
    if (!token) {
      navigate("/modelo/login");
      return;
    }
    
    try {
      setIsLoadingUser(true);
      const response = await fetch(`${getApiUrl()}/api/auth/me`, {
        credentials: "include",
        headers: {
          "Authorization": `Bearer ${token}`
        }
      });
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        
        // If user already has a profile, redirect to dashboard
        if (userData.has_profile) {
          navigate("/modelo/dashboard");
        }
      } else {
        console.error("Auth failed:", response.status);
        localStorage.removeItem("jglinks_session");
        navigate("/modelo/login");
      }
    } catch (e) {
      console.error("Fetch error:", e);
      navigate("/modelo/login");
    } finally {
      setIsLoadingUser(false);
    }
  };

  // Show loading while fetching user
  if (isLoadingUser) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!/^[a-zA-Z0-9_]+$/.test(username)) {
      toast.error("El username solo puede contener letras, números y guiones bajos");
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(`${getApiUrl()}/api/auth/set-username`, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
          "Authorization": `Bearer ${localStorage.getItem("jglinks_session")}`
        },
        credentials: "include",
        body: JSON.stringify({ username })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || "Error al guardar username");
      }

      toast.success("¡Username guardado!");
      navigate("/modelo/crear-perfil", { state: { user: { ...user, username } } });
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black flex flex-col">
      {/* Header */}
      <header className="p-6">
        <Link to="/">
          <img src={LOGO_URL} alt="JG Models" className="h-10" />
        </Link>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="w-20 h-20 rounded-full bg-white/10 mx-auto mb-4 overflow-hidden">
              {user?.picture ? (
                <img src={user.picture} alt="" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-2xl text-white/40">
                  {user?.name?.[0] || "?"}
                </div>
              )}
            </div>
            <h1 className="text-3xl font-bold text-white mb-2">
              ¡Hola, {user?.name || "Modelo"}!
            </h1>
            <p className="text-white/60">
              Elige tu username único para tu perfil
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="username" className="text-white/80">Username</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40">@</span>
                <Input
                  id="username"
                  type="text"
                  placeholder="tu_username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ""))}
                  required
                  className="h-12 pl-8 bg-white/5 border-white/10 text-white placeholder:text-white/30"
                />
              </div>
              <p className="text-sm text-white/40">
                Tu perfil estará en: <span className="text-white/60">jglinks.com/{username || "username"}</span>
              </p>
            </div>

            <Button
              type="submit"
              disabled={isLoading || !username}
              className="w-full h-12 bg-white text-black hover:bg-white/90"
            >
              {isLoading ? "Guardando..." : "Continuar"}
            </Button>
          </form>
        </div>
      </main>
    </div>
  );
};

export default CompletarPerfil;
